import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Loader2, Trash2, LayoutTemplate } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { AppHeader } from "@/components/AppHeader";
import { FloorPlan2D } from "@/components/FloorPlan2D";
import { SiteFields, DEFAULT_SITE } from "@/components/SitePanel";
import { floorView, roomCount } from "@/lib/walls";
import { DesignMemory } from "@/components/DesignMemory";
import { useBilling, startCheckout, parseSqft, money, confirmCharge } from "@/components/Billing";
import { BillingStrip } from "@/components/BillingWidgets";
import { PriceQuote } from "@/components/PriceQuote";
import { useAuth } from "@/context/AuthContext";

const CHIPS = [
  "1500 sq ft 3 bed 2 bath single-storey home",
  "950 sq ft urban loft, 1 bed + office",
  "2200 sq ft family home with 2-car garage",
  "600 sq ft backyard ADU, 1 bed",
];

const ProjectCard = ({ p, onOpen, onDelete }) => (
  <div className="group relative border border-[var(--line)] bg-[var(--surface)] hover:border-[var(--cyan)]/60 transition-colors cursor-pointer" onClick={onOpen} data-testid={`project-card-${p.project_id}`}>
    <div className="aspect-[4/3] blueprint-grid border-b border-[var(--line)] p-4 grid place-items-center">
      {p.plan ? <FloorPlan2D plan={floorView(p.plan, 0)} compact bg="#121824" /> : <span className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">Awaiting draft</span>}
    </div>
    <div className="p-4">
      <h3 className="font-semibold font-display truncate" data-testid="project-card-title">{p.title}</h3>
      <div className="mt-2 flex items-center gap-3 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
        {p.plan && <span className="text-[var(--cyan)]">{Math.round(p.plan.total_area)} SF</span>}
        {p.plan && <span>{roomCount(p.plan)} rooms</span>}
        {p.plan?.floors.length > 1 && <span className="text-[var(--amber)]">{p.plan.floors.length} levels</span>}
        <span>v{p.version}</span>
      </div>
    </div>
    <button data-testid={`delete-project-${p.project_id}`} onClick={(e) => { e.stopPropagation(); if (window.confirm(`Delete "${p.title}"?`)) onDelete(); }}
      className="absolute top-3 right-3 w-8 h-8 grid place-items-center glass border border-[var(--line)] lg:opacity-0 lg:group-hover:opacity-100 hover:text-red-400 transition-opacity"><Trash2 size={14} /></button>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [projects, setProjects] = useState(null);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [useLot, setUseLot] = useState(false);
  const [site, setSite] = useState(DEFAULT_SITE);
  const [sqft, setSqft] = useState("");
  const [pq, setPq] = useState({ code: "", total: 0 });
  const { billing } = useBilling();
  const admin = billing?.is_admin;
  const onPrompt = (v) => { setPrompt(v); const n = parseSqft(v); if (n) setSqft(String(n)); };
  const sq = Number(sqft) || 0;
  const draft = () => {
    if (!prompt.trim()) return;
    if (!admin && (sq < 300 || sq > 20000)) { toast.error("Enter the square footage (300–20,000 sq ft)"); return; }
    create({ prompt, site: useLot ? site : null, sqft: sq || null });
  };

  const load = () => api.get("/projects").then(({ data }) => setProjects(data));
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (projects && window.location.hash === "#dna") setTimeout(() => document.getElementById("dna")?.scrollIntoView({ behavior: "smooth" }), 300);
  }, [projects]);

  const create = async (body) => {
    setBusy(true);
    try {
      if (!admin && !body.from_sample && pq.total > 0 && !confirmCharge(billing, pq.total)) { setBusy(false); return; }
      const { data } = await api.post("/projects", body);
      if (!admin && !body.from_sample) {
        const pay = await startCheckout("/billing/draft-checkout", { project_id: data.project_id, code: pq.code });
        if (pay.paid) { toast.success(pay.amount ? `Charged ${money(pay.amount)} to your card on file` : "Covered by your discount - no charge"); navigate(`/project/${data.project_id}`); }
      } else navigate(`/project/${data.project_id}`);
    } catch (e) { toast.error(errMsg(e)); setBusy(false); }
  };
  const remove = async (id) => {
    await api.delete(`/projects/${id}`);
    toast.success("Project deleted");
    load();
  };

  return (
    <div className="min-h-screen" data-testid="dashboard-page">
      <AppHeader />
      <section className="blueprint-grid border-b border-[var(--line)]">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-16 md:py-20">
          <div className="rise flex flex-wrap items-center justify-between gap-3">
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--cyan)]">Studio · {user?.name}</p>
            <BillingStrip />
          </div>
          <h1 className="rise d1 mt-4 text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight">What are we drafting?</h1>
          <form noValidate className="rise d2 mt-10 max-w-3xl" onSubmit={(e) => { e.preventDefault(); draft(); }}>
            <div className="flex flex-col sm:flex-row gap-0 border border-[var(--line)] focus-within:border-[var(--cyan)] bg-[var(--bg)] transition-colors">
              <textarea data-testid="new-draft-prompt-input" rows={2} value={prompt} onChange={(e) => onPrompt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); draft(); } }}
                placeholder="e.g. I need a draft for a 1500 square foot home, 3 bedrooms, open kitchen…"
                className="flex-1 bg-transparent resize-none outline-none p-5 text-base placeholder:text-[var(--muted)]/60" />
              <label className="sm:w-36 flex flex-col justify-center px-4 py-3 border-t sm:border-t-0 sm:border-l border-[var(--line)]">
                <span className="font-mono text-[9px] uppercase tracking-widest text-[var(--muted)]">Sq ft</span>
                <input data-testid="new-draft-sqft-input" type="number" min="300" max="20000" value={sqft} onChange={(e) => setSqft(e.target.value)} placeholder="1500"
                  className="bg-transparent outline-none text-lg font-mono w-full" />
              </label>
              <button data-testid="generate-draft-btn" disabled={busy || !prompt.trim()} className="sm:w-52 py-4 bg-[var(--cyan)] text-[var(--bg)] font-semibold font-display flex items-center justify-center gap-2 disabled:opacity-40 hover:brightness-110 transition-[filter,opacity]">
                {busy ? <Loader2 size={18} className="animate-spin" /> : admin ? <>Generate draft <ArrowRight size={18} /></> : <>{sq ? `Pay ${money(pq.sqft === sq ? pq.total : sq)} & draft` : "Pay & draft"} <ArrowRight size={18} /></>}
              </button>
            </div>
            <div className="mt-4 flex flex-wrap gap-2">
              {CHIPS.map((c, i) => (
                <button type="button" key={c} onClick={() => setPrompt(c)} data-testid={`prompt-chip-${i}`}
                  className="text-xs px-3 py-1.5 border border-[var(--line)] bg-[var(--bg)]/70 text-[var(--muted)] hover:text-[var(--cyan)] hover:border-[var(--cyan)] transition-colors">{c}</button>
              ))}
              <button type="button" data-testid="toggle-lot-btn" onClick={() => setUseLot((u) => !u)}
                className={`text-xs px-3 py-1.5 border transition-colors ${useLot ? "border-[var(--amber)] text-[var(--amber)] bg-[var(--amber)]/10" : "border-dashed border-[var(--line)] bg-[var(--bg)]/70 text-[var(--muted)] hover:text-[var(--amber)]"}`}>
                {useLot ? "✓ Fit to my lot" : "+ Add lot & setbacks"}
              </button>
            </div>
            {useLot && (
              <div className="mt-4 p-4 border border-[var(--amber)]/40 bg-[var(--bg)]/80 max-w-xl rise" data-testid="new-draft-lot-fields">
                <SiteFields value={site} onChange={setSite} />
              </div>
            )}
            {!admin && <p className="mt-3 text-xs text-[var(--muted)]" data-testid="draft-price-note">
              {sq >= 300 ? <><span className="text-[var(--amber)] font-mono" data-testid="draft-price-estimate">{sq.toLocaleString()} sq ft × $1 = {money(sq)}{pq.sqft === sq && pq.total < sq ? ` → ${money(pq.total)} after discounts` : ""}</span> · charged {!billing ? "" : billing.card ? `to ${billing.card.brand} •••• ${billing.card.last4}` : "at checkout"} before the AI starts. </> : "Drafts are $1 per sq ft. "}
              Revisions are free unless they grow the home beyond its licensed size.</p>}
            {!admin && sq >= 300 && <div className="mt-3 max-w-md"><PriceQuote sqft={sq} onChange={setPq} /></div>}
          </form>
        </div>
      </section>
      <section className="max-w-[1400px] mx-auto px-6 lg:px-12 py-14">
        <div className="flex items-end justify-between mb-8 gap-4">
          <div>
            <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Your drafts</p>
            <h2 className="mt-2 text-2xl sm:text-3xl font-bold tracking-tight">{projects ? `${projects.length} project${projects.length === 1 ? "" : "s"}` : "Loading…"}</h2>
          </div>
          <button data-testid="use-template-btn" onClick={() => create({ from_sample: true })} disabled={busy}
            className="flex items-center gap-2 text-sm px-4 py-2.5 border border-[var(--line)] hover:border-[var(--amber)] hover:text-[var(--amber)] transition-colors">
            <LayoutTemplate size={16} /> Start from template
          </button>
        </div>
        {projects && projects.length === 0 && <p className="text-[var(--muted)]" data-testid="projects-empty">No drafts yet — describe a building above.</p>}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6" data-testid="projects-grid">
          {projects?.map((p) => <ProjectCard key={p.project_id} p={p} onOpen={() => navigate(`/project/${p.project_id}`)} onDelete={() => remove(p.project_id)} />)}
        </div>
      </section>
      <section id="dna" className="max-w-[1400px] mx-auto px-6 lg:px-12 pb-20" data-testid="design-dna-section">
        <p className="font-mono text-xs uppercase tracking-[0.3em] text-[var(--muted)]">Design DNA</p>
        <h2 className="mt-2 mb-8 text-2xl sm:text-3xl font-bold tracking-tight">The AI learns how you lay out plans</h2>
        <DesignMemory />
      </section>
    </div>
  );
}
